// src/hooks/useUpdateDetails.ts
// Hook used by <UpdateDetailsForm /> to build FormData + call API,
// mirroring the status pattern your forms already use, without rethrowing.

import { useState, useCallback } from "react";
import { userApi } from "../api/user";

type Status = {
  loading: boolean;
  error: string | null;
  success: string | null;
};

type Input = {
  name: string;
  last_name: string;
  email: string;
  location: string;
  city: string;
  country: string;
  institute: string;
  description: string;
  image: File | null;
};

function extractApiError(e: any): string {
  // Use the standard Axios error structure (e.response for HTTP errors)
  const status = e?.response?.status;
  const data = e?.response?.data;

  // Use data?.errors for Laravel validation errors, or data.message for other messages
  const validationErrors = data?.errors;

  let msg =
    data?.message ||
    data?.error ||
    e?.message ||
    "Request failed. See console for details.";

  // Attempt to parse validation errors into a single string
  if (validationErrors) {
    const allMessages = Object.values(validationErrors).flat();
    if (allMessages.length > 0) {
      msg = allMessages.join(" • ");
    }
  }

  if (status) return `${msg} (HTTP ${status})`;
  return msg;
}

export function useUpdateDetails() {
  const [status, setStatus] = useState<Status>({
    loading: false,
    error: null,
    success: null,
  });

  const clearStatus = useCallback(() => {
    setStatus({ loading: false, error: null, success: null });
  }, []);

  const submitUpdateDetails = useCallback(async (data: Input) => {
    setStatus({ loading: true, error: null, success: null });

    const fd = new FormData();

    // Fix: Required and location/institute fields must be appended unconditionally
    // to satisfy Laravel's 'required' and to pass empty strings for nullable fields.
    fd.append("name", data.name);
    fd.append("last_name", data.last_name);
    fd.append("email", data.email);
    fd.append("institute", data.institute);
    fd.append("location", data.location);

    fd.append("city", data.city);
    fd.append("country", data.country);
    if (data.description) fd.append("description", data.description);

    // Optional file
    if (data.image instanceof File) {
      fd.append("image", data.image);
    }

    // Extra safety: if the form is truly empty, short-circuit
    // NOTE: If you pass empty strings above, this check might fail unless you
    // update it to check for meaningful changes. For now, we rely on the backend.
    if (![...fd.keys()].length) {
      setStatus({
        loading: false,
        error: "Nothing to update. Please change a field before submitting.",
        success: null,
      });
      return null;
    }

    try {
      // userApi.updateDetails now already returns the *body* (not { data }).
      const body = await userApi.updateDetails(fd);

      setStatus({
        loading: false,
        error: null,
        success: "Profile updated successfully!",
      });

      // Do NOT rethrow — prevents "Uncaught (in promise)"
      return body;
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error("[useUpdateDetails] API error:", {
        status: e?.response?.status,
        data: e?.response?.data,
      });

      const message = extractApiError(e);
      setStatus({ loading: false, error: message, success: null });

      // Do not rethrow — caller won't crash
      return null;
    }
  }, []);

  return { status, clearStatus, submitUpdateDetails };
}
